import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { batimentColor, fmtDate } from "@/lib/helpers";
import { ArrowLeft, Save, Lock, Trash2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MOTIFS = ["Fêlés", "Sales", "Sang", "Cassés", "Calibre non conforme", "Sous-poids", "Autre"];
const BATIMENTS = { P1: "2 FR NCK 01", P2: "2 FR NCK 02", P3: "2 FR NCK 03" };

export default function ModifierPalette() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [palette, setPalette] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get(`/palettes/${id}`).then((r) => {
      setPalette(r.data);
      setForm({
        type: r.data.type,
        batiment: r.data.batiment,
        date_ponte: r.data.date_ponte,
        categorie: r.data.categorie || "1",
        motif: r.data.motif || "",
        motif_autre: r.data.motif_autre || "",
        poids_brut: String(r.data.poids_brut),
        tare: String(r.data.tare),
      });
    });
  }, [id]);

  if (!palette || !form) return <div className="text-slate-400">Chargement…</div>;

  const isDec = form.type === "DECLASSEE";
  const typeChanged = form.type !== palette.type;
  const isAdmin = user?.role === "admin";
  const locked = palette.statut === "expediee";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const brut = parseFloat(String(form.poids_brut).replace(",", ".")) || 0;
  const tare = parseFloat(String(form.tare).replace(",", ".")) || 0;
  const net = brut > tare ? +(brut - tare).toFixed(1) : 0;

  const save = async () => {
    if (brut <= tare) return toast.error("Le poids brut doit être supérieur à la tare.");
    if (isDec && !form.motif) return toast.error("Le motif est obligatoire.");
    if (typeChanged && !window.confirm(`Changer le type attribue un NOUVEAU numéro de palette (l'ancien ${palette.code_qr} est abandonné). L'étiquette devra être réimprimée. Confirmer ?`)) return;
    setSaving(true);
    try {
      await api.put(`/palettes/${id}`, {
        type: typeChanged ? form.type : undefined,
        batiment: form.batiment,
        date_ponte: form.date_ponte,
        categorie: isDec ? undefined : form.categorie,
        motif: isDec ? form.motif : undefined,
        motif_autre: isDec ? (form.motif === "Autre" ? form.motif_autre : "") : undefined,
        poids_brut: brut,
        tare,
      });
      toast.success(typeChanged ? "Type modifié — nouveau numéro attribué, réimprimez l'étiquette" : `Palette ${palette.code_qr} rectifiée`);
      navigate(`/palette/${id}/etiquette`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer définitivement la palette ${palette.code_qr} ?`)) return;
    try {
      await api.delete(`/palettes/${id}`);
      toast.success("Palette supprimée");
      navigate("/palettes");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="modifier-palette">
      <div className="flex items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/palette/${id}/etiquette`)} className="h-12 font-bold">
          <ArrowLeft className="w-5 h-5 mr-1" /> Retour
        </Button>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Rectification</div>
          <div className="font-mono font-black text-xl text-slate-900" data-testid="modifier-palette-code">{palette.code_qr}</div>
        </div>
      </div>

      {locked && (
        <div data-testid="palette-locked-banner" className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 flex gap-3 items-start">
          <Lock className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <div className="font-bold text-amber-900">Palette expédiée — verrouillée</div>
            <div className="text-sm text-amber-800">
              Cette palette figure sur le BL <span className="font-mono font-bold">{palette.bl_numero}</span>.
              Pour la rectifier, annulez d'abord ce bon de livraison (administrateur).
            </div>
          </div>
        </div>
      )}

      <div className={`rounded-2xl bg-white border-2 p-5 sm:p-6 space-y-5 ${isDec ? "border-rose-300" : "border-slate-200"}`}>
        {isAdmin && !locked ? (
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Type de palette (admin)</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button type="button" data-testid="modifier-type-p-btn" onClick={() => set("type", "P")}
                className={`min-h-[56px] rounded-xl border-4 font-heading font-black uppercase ${form.type === "P" ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500"}`}>
                Conforme P
              </button>
              <button type="button" data-testid="modifier-type-d-btn" onClick={() => set("type", "DECLASSEE")}
                className={`min-h-[56px] rounded-xl border-4 font-heading font-black uppercase ${form.type === "DECLASSEE" ? "border-rose-600 bg-rose-50 text-rose-800" : "border-slate-200 text-slate-500"}`}>
                Déclassée
              </button>
            </div>
            {typeChanged && (
              <div data-testid="modifier-type-warning" className="mt-2 flex items-start gap-2 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded-lg p-2">
                <AlertTriangle className="w-4 h-4 shrink-0" /> Un nouveau numéro {form.type === "P" ? "AUT-…" : "AUT-D-…"} sera attribué ; l'ancien {palette.code_qr} sera abandonné. Réimprimez l'étiquette.
              </div>
            )}
          </div>
        ) : (
          <div className={`inline-block px-3 py-1 rounded font-bold text-xs uppercase ${isDec ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>
            {isDec ? "Palette déclassée" : "Palette conforme P"} — type modifiable par l'administrateur uniquement
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Bâtiment</Label>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {Object.entries(BATIMENTS).map(([b, code]) => (
              <button key={b} type="button" disabled={locked}
                data-testid={`modifier-batiment-${b.toLowerCase()}-btn`}
                onClick={() => set("batiment", b)}
                className={`min-h-[72px] rounded-xl border-4 flex flex-col items-center justify-center disabled:opacity-50 ${
                  form.batiment === b ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
                <span className={`px-3 rounded-lg font-heading font-black text-xl ${batimentColor(b)}`}>{b}</span>
                <span className="text-[10px] text-slate-500 font-mono mt-1">{code}</span>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Date de ponte</Label>
          <Input data-testid="modifier-date-ponte" type="date" max={today} disabled={locked}
            value={form.date_ponte} onChange={(e) => set("date_ponte", e.target.value)} className="mt-1 h-12 text-lg" />
        </div>

        {isDec ? (
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-rose-600">Motif de déclassement</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {MOTIFS.map((m) => (
                <button key={m} type="button" disabled={locked} data-testid={`modifier-motif-${m}`}
                  onClick={() => set("motif", m)}
                  className={`min-h-[48px] rounded-xl border-2 font-semibold text-sm px-2 disabled:opacity-50 ${
                    form.motif === m ? "bg-rose-600 border-rose-700 text-white" : "border-slate-200"}`}>
                  {m}
                </button>
              ))}
            </div>
            {form.motif === "Autre" && (
              <Input data-testid="modifier-motif-autre" placeholder="Préciser le motif" disabled={locked}
                value={form.motif_autre} onChange={(e) => set("motif_autre", e.target.value)} className="mt-3 h-12" />
            )}
          </div>
        ) : (
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Catégorie</Label>
            <Input data-testid="modifier-categorie" disabled={locked} value={form.categorie}
              onChange={(e) => set("categorie", e.target.value)} className="mt-1 h-12 text-lg" />
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Poids brut (kg)</Label>
            <Input data-testid="modifier-poids-brut" type="number" step="0.1" inputMode="decimal" disabled={locked}
              value={form.poids_brut} onChange={(e) => set("poids_brut", e.target.value)} className="mt-1 h-12 text-lg font-mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Tare (kg)</Label>
            <Input data-testid="modifier-tare" type="number" step="0.1" inputMode="decimal" disabled={locked}
              value={form.tare} onChange={(e) => set("tare", e.target.value)} className="mt-1 h-12 text-lg font-mono" />
          </div>
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Poids net</div>
            <div data-testid="modifier-poids-net" className="font-mono font-black text-2xl text-emerald-700">{net.toFixed(1)}</div>
            <div className="text-[10px] text-slate-400 font-bold">kg</div>
          </div>
        </div>
      </div>

      {!locked && (
        <div className="flex flex-wrap gap-3">
          <Button data-testid="modifier-delete-btn" variant="outline" onClick={remove}
            className="h-14 font-bold border-rose-300 text-rose-700 hover:bg-rose-50">
            <Trash2 className="w-5 h-5 mr-1" /> Supprimer
          </Button>
          <Button data-testid="modifier-save-btn" onClick={save} disabled={saving}
            className="h-14 flex-1 min-w-[200px] text-base font-bold bg-slate-900 hover:bg-slate-800">
            <Save className="w-5 h-5 mr-1" /> Enregistrer<span className="hidden sm:inline">&nbsp;la rectification</span>
          </Button>
        </div>
      )}

      {palette.modifications?.length > 0 && (
        <div className="rounded-2xl bg-white border-2 border-slate-200 p-5" data-testid="palette-historique-modifs">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Journal des modifications</div>
          <ul className="space-y-2 text-sm">
            {[...palette.modifications].reverse().map((m, i) => (
              <li key={i} className="border-l-4 border-slate-200 pl-3">
                <div className="font-bold text-slate-800">
                  {fmtDate(m.at.slice(0, 10))} {m.at.slice(11, 16)} — {m.by}
                </div>
                <div className="text-slate-600">
                  {Object.entries(m.changes).map(([k, v]) => (
                    <span key={k} className="mr-3">
                      <span className="font-semibold">{LABELS[k] || k}</span> : {String(v.avant ?? "—")} → {String(v.apres ?? "—")}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const LABELS = {
  type: "Type", code_qr: "N° palette",
  batiment: "Bâtiment", batiment_code: "Code bât.", date_ponte: "Ponte", categorie: "Catégorie",
  motif: "Motif", motif_autre: "Motif (autre)", poids_brut: "Brut", tare: "Tare", poids_net: "Net",
  bl_annule: "BL annulé",
};
